export type TextLineMeasurements = Readonly<{
  lineBoxes: readonly DOMRect[];
  lineCount: number;
}>;

export function measureTextLines(el: HTMLElement): TextLineMeasurements {
  // For `display: block` elements, `el.getClientRects()` returns only
  // 1 box.  So we create an inline range which will return a box for
  // each line of text.
  const range = new Range();
  range.selectNodeContents(el);
  const lineBoxes = [...range.getClientRects()]
    .filter(
      // Ignore 0x0 boxes, which are sometimes rendered between lines.
      // DO NOT IGNORE 0px wide boxes, which are empty lines themselves.
      (box) => box.height > 1e-6,
    )
    .sort((a, b) => a.top - b.top)
    .reduce(reduceMergeSameLine, []);
  return { lineCount: lineBoxes.length, lineBoxes };
}

function reduceMergeSameLine(acc: DOMRect[], next: DOMRect): DOMRect[] {
  const last = acc[acc.length - 1];
  if (last == null || boxVerticalIou(last, next) < 0.2) {
    // not overlapping enough to be considered the same line
    acc.push(next);
    return acc;
  }
  const newX = Math.min(last.x, next.x);
  const newWidth = Math.max(last.right, next.right) - newX;
  const newY = Math.min(last.y, next.y);
  const newHeight = Math.max(last.bottom, next.bottom) - newY;
  acc[acc.length - 1] = new DOMRect(newX, newY, newWidth, newHeight);
  return acc;
}

/** IOU = intersection over union */
function boxVerticalIou(box1: DOMRect, box2: DOMRect): number {
  const yIntersection =
    Math.min(box1.bottom, box2.bottom) - Math.max(box1.top, box2.top);
  const yUnion =
    Math.max(box1.bottom, box2.bottom) - Math.min(box1.top, box2.top);
  return yIntersection / yUnion;
}
